import React, { useState, useEffect, useRef } from 'react';
import { Search, X, ChevronDown, Building2, Briefcase, FolderKanban, Layers, Box, FileText, ListTodo, Users, Target, Truck, Server, Database } from 'lucide-react';
import { api } from '../api/client';

// Entity type icons
const ENTITY_ICONS = {
  portfolio: Building2,
  program: Briefcase,
  project: FolderKanban,
  epic: Layers,
  feature: Box,
  story: FileText,
  task: ListTodo,
  team: Users,
  initiative: Target,
  vendor: Truck,
  asset: Database,
  system: Server
};

// Entity type colors
const ENTITY_COLORS = {
  portfolio: 'bg-purple-100 text-purple-700 border-purple-200',
  program: 'bg-blue-100 text-blue-700 border-blue-200',
  project: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  epic: 'bg-violet-100 text-violet-700 border-violet-200',
  feature: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  story: 'bg-green-100 text-green-700 border-green-200',
  task: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  team: 'bg-orange-100 text-orange-700 border-orange-200',
  initiative: 'bg-pink-100 text-pink-700 border-pink-200',
  vendor: 'bg-red-100 text-red-700 border-red-200',
  asset: 'bg-teal-100 text-teal-700 border-teal-200',
  system: 'bg-slate-100 text-slate-700 border-slate-200'
};

const ALL_ENTITY_TYPES = [
  'portfolio', 'program', 'project', 'epic', 'feature', 'story', 'task',
  'team', 'initiative', 'vendor', 'asset', 'system'
];

/**
 * Unified Entity Picker Component
 * 
 * Props:
 * - multi: boolean - Allow multiple selections
 * - allowedTypes: string[] - Restrict to specific entity types
 * - value: EntityRef[] - Current selected entities [{ entity_type, entity_id, display }]
 * - onChange: (val: EntityRef[]) => void - Callback when selection changes
 * - placeholder: string - Input placeholder text
 * - disabled: boolean - Disable the picker
 */
const EntityPicker = ({ 
  multi = true, 
  allowedTypes = ALL_ENTITY_TYPES,
  value = [], 
  onChange,
  placeholder = 'Search and link entities...',
  disabled = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search entities when search text changes
  useEffect(() => {
    const searchEntities = async () => {
      if (!isOpen) return;
      
      setLoading(true);
      try {
        const types = selectedType ? selectedType : allowedTypes.join(',');
        const res = await api.getEntities({ 
          types, 
          search: search || undefined,
          limit: 50 
        });
        setResults(res.data?.data?.entities || []);
      } catch (error) {
        console.error('Failed to search entities:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchEntities, 300);
    return () => clearTimeout(debounce);
  }, [search, isOpen, selectedType, allowedTypes]);

  const handleSelect = (entity) => {
    if (disabled) return;
    
    const newValue = multi 
      ? [...value, entity]
      : [entity];
    
    // Avoid duplicates
    const unique = newValue.filter((v, i, arr) => 
      arr.findIndex(a => a.entity_type === v.entity_type && a.entity_id === v.entity_id) === i
    );
    
    onChange(unique);
    
    if (!multi) {
      setIsOpen(false);
    }
    setSearch('');
  };

  const handleRemove = (entity) => {
    if (disabled) return;
    onChange(value.filter(v => !(v.entity_type === entity.entity_type && v.entity_id === entity.entity_id)));
  };

  const isSelected = (entity) => {
    return value.some(v => v.entity_type === entity.entity_type && v.entity_id === entity.entity_id);
  };

  const getIcon = (type) => {
    const Icon = ENTITY_ICONS[type] || Box;
    return Icon;
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Selected chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {value.map((entity, idx) => {
            const Icon = getIcon(entity.entity_type);
            return (
              <div 
                key={`${entity.entity_type}-${entity.entity_id}-${idx}`}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm border ${ENTITY_COLORS[entity.entity_type] || 'bg-gray-100 text-gray-700 border-gray-200'}`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="font-medium capitalize">{entity.entity_type}:</span>
                <span>{entity.display || entity.entity_id}</span>
                {!disabled && (
                  <button 
                    onClick={() => handleRemove(entity)}
                    className="ml-1 hover:bg-black/10 rounded p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Input */}
      <div 
        className={`flex items-center gap-2 px-3 py-2 border rounded-lg transition-colors ${
          isOpen ? 'border-[var(--clr-brand-primary)] ring-2 ring-[var(--clr-brand-primary)]/20' : 'border-[var(--clr-border-light)]'
        } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-text'}`}
        onClick={() => !disabled && setIsOpen(true)}
      >
        <Search className="w-4 h-4 text-[var(--clr-text-muted)]" />
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1 bg-transparent outline-none text-sm text-[var(--clr-text-primary)] placeholder:text-[var(--clr-text-muted)]"
          data-testid="entity-picker-input"
        />
        <ChevronDown className={`w-4 h-4 text-[var(--clr-text-muted)] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[var(--clr-border-light)] rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Type filter tabs */}
          <div className="flex flex-wrap gap-1 p-2 border-b border-[var(--clr-border-light)] bg-[var(--clr-surface-subtle)]">
            <button
              onClick={() => setSelectedType(null)}
              className={`px-2 py-1 text-xs rounded ${!selectedType ? 'bg-[var(--clr-brand-primary)] text-white' : 'bg-white text-[var(--clr-text-secondary)] hover:bg-gray-100'}`}
            >
              All
            </button>
            {allowedTypes.slice(0, 8).map(type => (
              <button
                key={type}
                onClick={() => setSelectedType(type)}
                className={`px-2 py-1 text-xs rounded capitalize ${selectedType === type ? 'bg-[var(--clr-brand-primary)] text-white' : 'bg-white text-[var(--clr-text-secondary)] hover:bg-gray-100'}`}
              >
                {type}
              </button>
            ))}
          </div>

          {/* Results */}
          <div className="max-h-60 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-[var(--clr-text-muted)]">
                <div className="animate-spin w-5 h-5 border-2 border-[var(--clr-brand-primary)] border-t-transparent rounded-full mx-auto mb-2"></div>
                Searching...
              </div>
            ) : results.length === 0 ? (
              <div className="p-4 text-center text-[var(--clr-text-muted)]">
                {search ? `No entities found for "${search}"` : 'Start typing to search...'}
              </div>
            ) : (
              results.map((entity, idx) => {
                const Icon = getIcon(entity.entity_type);
                const selected = isSelected(entity);
                
                return (
                  <button
                    key={`${entity.entity_type}-${entity.entity_id}-${idx}`}
                    onClick={() => !selected && handleSelect(entity)}
                    disabled={selected}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[var(--clr-surface-subtle)] transition-colors ${selected ? 'bg-[var(--clr-surface-subtle)] opacity-50' : ''}`}
                    data-testid={`entity-option-${entity.entity_type}-${idx}`}
                  >
                    <div className={`p-1.5 rounded ${ENTITY_COLORS[entity.entity_type]?.split(' ')[0] || 'bg-gray-100'}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium uppercase text-[var(--clr-text-muted)]">{entity.entity_type}</span>
                        {selected && <span className="text-xs text-green-600">(Selected)</span>}
                      </div>
                      <p className="text-sm text-[var(--clr-text-primary)] truncate">{entity.display}</p>
                      {entity.description && (
                        <p className="text-xs text-[var(--clr-text-muted)] truncate">{entity.description}</p>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EntityPicker;

// Export entity colors and icons for reuse
export { ENTITY_ICONS, ENTITY_COLORS, ALL_ENTITY_TYPES };
